import {
  ProductionVolume,
  useCreateProductionVolumeMutation,
  useDeleteProductionVolumeMutation,
  useUpdateProductionVolumeMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseProductionVolumeMutationProps = {}

export type UseProductionVolumeMutationResult = {
  createProductionVolume: (productionVolume: Partial<ProductionVolume>, onSuccess: (() => void) | undefined) => void
  updateProductionVolume: (productionVolume: Partial<ProductionVolume>, onSuccess: (() => void) | undefined) => void
  deleteProductionVolume: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useProductionVolumeMutation = (props: UseProductionVolumeMutationProps = {}): UseProductionVolumeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createProductionVolume, {isLoading: isCreating}] = useCreateProductionVolumeMutation()
  const [_updateProductionVolume, {isLoading: isUpdating}] = useUpdateProductionVolumeMutation()
  const [_deleteProductionVolume, {isLoading: isDeleting}] = useDeleteProductionVolumeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useProductionVolumeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entityproductionvolume", "Production Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useProductionVolumeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useProductionVolumeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entityproductionvolume", "Production Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useProductionVolumeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useProductionVolumeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entityproductionvolume", "Production Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useProductionVolumeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entityproductionvolume", "Production Volume") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entityproductionvolume", "Production Volume") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entityproductionvolume", "Production Volume") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteProductionVolume({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteProductionVolume, handleDeleteResult])

  const createProductionVolume = useCallback((productionVolume: Partial<ProductionVolume>, onSuccess: (() => void) | undefined) => {
    const promise = _createProductionVolume(productionVolume).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createProductionVolume, handleCreateResult])

  const updateProductionVolume = useCallback((productionVolume: Partial<ProductionVolume>, onSuccess: (() => void) | undefined) => {
    const promise = _updateProductionVolume(productionVolume).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateProductionVolume, handleUpdateResult])

  const deleteProductionVolume = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entityproductionvolume", "Production Volume"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deleteproductionvolume.confirm", "Are you sure you want to delete this production volume?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createProductionVolume: createProductionVolume,
    updateProductionVolume: updateProductionVolume,
    deleteProductionVolume: deleteProductionVolume,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
